import React from 'react';
import { Box, Container, Typography, Button, Stack, Divider } from '@mui/material';
import Header from '../Header';
import SEO from '../SEO';
import Footer from '../Footer';
import { useNavigate } from 'react-router-dom';

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 6 }}>
      <Typography variant="h3" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 700, fontSize: { xs: '1.4rem', md: '1.8rem' }, color: '#f0f0f8', letterSpacing: '-0.02em', mb: 2.5 }}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function Para({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#9499b0', lineHeight: 1.8, mb: 2 }}>
      {children}
    </Typography>
  );
}

function Quote({ text, context }: { text: string; context: string }) {
  return (
    <Box sx={{ p: 3, bgcolor: '#13131f', border: '1px solid rgba(140,82,255,0.2)', borderLeft: '3px solid #8c52ff', borderRadius: 2, mb: 3 }}>
      <Typography sx={{ fontFamily: '"Libre Baskerville", Georgia, serif', fontSize: { xs: 15, md: 17 }, color: '#d0d0e0', lineHeight: 1.7, fontStyle: 'italic', mb: 0.75 }}>
        "{text}"
      </Typography>
      <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: 'rgba(148,153,176,0.6)' }}>
        {context}
      </Typography>
    </Box>
  );
}

export default function BlogArizonaCoolidgePage() {
  const navigate = useNavigate();

  return (
    <Box sx={{ bgcolor: '#0a0a12', minHeight: '100vh' }}>
      <SEO title="Arizona Coolidge Daycare Lawsuit — What the Audio Captured" description="Parents sued after recordings revealed verbal threats and abuse at an Arizona daycare. How audio AI would have flagged this pattern weeks before the lawsuit." path="/blog/arizona-coolidge-daycare-lawsuit" type="article" />
      <Header onBookDemo={() => navigate('/contact')} />
      <Container maxWidth="sm" sx={{ py: { xs: 6, md: 10 }, px: 3 }}>

        {/* Breadcrumb */}
        <Box sx={{ display: 'flex', gap: 1, mb: 4, alignItems: 'center' }}>
          <Typography onClick={() => navigate('/blog')} sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', cursor: 'pointer', '&:hover': { color: '#5ce1e6' } }}>Blog</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: 'rgba(148,153,176,0.4)' }}>›</Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0' }}>Audio Detection</Typography>
        </Box>

        {/* Tag badge */}
        <Box sx={{ px: 1.5, py: 0.5, bgcolor: 'rgba(140,82,255,0.08)', border: '1px solid rgba(140,82,255,0.2)', borderRadius: 1, display: 'inline-block', mb: 2.5 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 11, color: '#8c52ff', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Audio Detection · 6 min read</Typography>
        </Box>

        <Typography variant="h1" sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: { xs: '1.8rem', md: '2.8rem' }, letterSpacing: '-0.03em', color: '#f0f0f8', mb: 3, lineHeight: 1.1 }}>
          She Threatened to Lock Them in a Dark Room. One Parent's Recording Caught It. AI Would Have Caught It First.
        </Typography>
        <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 16, color: '#9499b0', mb: 5, lineHeight: 1.65 }}>
          Parents of 19 children in Coolidge, Arizona filed suit after a caregiver found on Facebook was caught threatening children with a dark room and verbal intimidation. A parent made the recording. No safety system was in place. This is exactly the threat pattern audio AI detects — before the damage reaches a courtroom.
        </Typography>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 5 }} />

        {/* YouTube embed */}
        <Box sx={{ position: 'relative', paddingTop: '56.25%', borderRadius: 2, overflow: 'hidden', mb: 5, border: '1px solid rgba(140,82,255,0.15)', boxShadow: '0 0 40px rgba(140,82,255,0.06)' }}>
          <Box
            component="iframe"
            src="https://www.youtube.com/embed/jlhkJPPmiQE"
            title="Arizona parents sue child care provider over alleged abuse — news coverage"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
          />
        </Box>

        <Section title="How it started: a Facebook search and a promise of affordability">
          <Para>
            Sara MacDowell was looking for childcare for her five children. She found the answer where millions of parents find it: Facebook. The caregiver was local, affordable, and described her operation as small — just a handful of kids, a dedicated playroom, a personal touch.
          </Para>
          <Para>
            "She was affordable, said she only watched a handful of kids. She had a playroom, and we decided to trust her with it," MacDowell said.
          </Para>
          <Para>
            That was the pitch. That was the vetting process. That was the entirety of what separated a family's most vulnerable members from a woman who would eventually be sued by the parents of 19 children — for neglect, emotional abuse, and intentional infliction of emotional distress.
          </Para>
          <Para>
            The facility was an in-home daycare in Coolidge, Arizona. It was not a licensed commercial center with inspections and oversight. It was a house, around the corner from the MacDowell home, run by a woman who had made herself easy to find and affordable to trust.
          </Para>
        </Section>

        <Section title="What the recordings captured">
          <Para>
            Parents began noticing behavioral changes in their children before they had any audio evidence. Children who had been calm and happy were returning home anxious, withdrawn, or newly afraid. The kind of changes that parents feel before they can name.
          </Para>
          <Para>
            One parent decided to find out what was happening. She captured audio recordings of the caregiver's interactions with the children. What those recordings documented was not ambiguous.
          </Para>

          <Quote
            text="I'm going to lock you up in a dark room."
            context="The caregiver, recorded by a parent. Directed at a child in her care. Coolidge, Arizona, 2026."
          />

          <Para>
            In another recording, the caregiver can be heard threatening the children directly. Not raising her voice in a moment of frustration — threatening. As a behavioral tool. As a way of managing young children who were in her care because their parents trusted her.
          </Para>
          <Para>
            The recordings became the evidentiary core of the lawsuit. Without them, the behavioral changes in the children would have remained visible only to the parents who lived with the consequences — and invisible to anyone in a position to act on them.
          </Para>
        </Section>

        <Section title="The threat pattern audio AI detects — at high confidence">
          <Para>
            Threatening language directed at children is one of the clearest audio signatures that behavioral AI is designed to flag. It is not subtle. It does not require context or interpretation. It is a specific, detectable pattern: an adult voice, directed at a child, using language categorized as intimidation, threat, or fear induction.
          </Para>
          <Para>
            SMCLA's audio detection identifies threatening language with high confidence — including explicit threats ("I'm going to lock you up"), verbal intimidation, raised-voice commands paired with fear-language, and vocal patterns that fall outside a worker's established baseline for normal caregiving communication.
          </Para>

          <Box sx={{ p: 3, bgcolor: 'rgba(140,82,255,0.04)', border: '1px solid rgba(140,82,255,0.15)', borderRadius: 2, mb: 3 }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#8c52ff', mb: 1.5 }}>
              What SMCLA audio AI flags in real time:
            </Typography>
            <Stack spacing={1}>
              {[
                'Explicit threats — "lock you up," "dark room," punitive confinement language — detected as high-priority events',
                'Intimidation patterns — vocal tone and content combinations that match fear-induction behavior toward children',
                'Caregiver deviation from baseline — a worker whose day-to-day tone with children shifts into threat mode is tracked against her own established pattern',
                'Child distress responses — crying, fearful vocalizations, or sudden silence following threatening language',
                'Cumulative frequency — one threat is flagged; a pattern of threats over days is escalated automatically',
              ].map((item, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1.5 }}>
                  <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#8c52ff', mt: 0.85, flexShrink: 0 }} />
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{item}</Typography>
                </Box>
              ))}
            </Stack>
          </Box>

          <Para>
            "I'm going to lock you up in a dark room" is not a borderline case. It is not an edge case requiring nuanced classification. It is a threat, in words, to a child. That single sentence — captured on a parent's phone after the fact — would have triggered an immediate alert the first time it was spoken inside a monitored environment.
          </Para>
        </Section>

        <Section title="The scenario that should have happened — weeks earlier">
          <Para>
            Parents noticed behavioral changes before any audio evidence existed. Children do not exhibit anxiety and withdrawal as a sudden response to a single incident. The threatening behavior that one parent eventually recorded was part of a pattern that had been building — and expressing itself in the behavior of children who could not yet name what was happening to them.
          </Para>

          <Stack spacing={2} sx={{ mb: 3 }}>
            {[
              {
                time: 'Day 1 of deployment',
                event: 'Baseline established',
                detail: "SMCLA maps the caregiver's normal communication patterns with children — tone, volume, language category, frequency of commands. This baseline is the measure against which everything that follows is evaluated.",
              },
              {
                time: 'First week of threatening behavior',
                event: 'Intimidation pattern flagged',
                detail: 'The audio AI detects language categorized as threatening or fear-inducing directed at children. A clip is sent to the owner — or in an in-home setting, surfaced to a monitoring parent — within 60 seconds of the event.',
              },
              {
                time: 'Before behavioral changes consolidate',
                event: 'Owner or parent acts',
                detail: 'The caregiver is confronted or removed. The pattern is stopped. The children do not carry the behavioral consequences home because the source of the threat was eliminated before it became chronic.',
              },
              {
                time: 'Never',
                event: 'The lawsuit does not happen',
                detail: '19 families do not sue. Children do not develop fear responses to caregiving environments. Parents do not spend months in legal proceedings trying to document something a sensor would have captured on day one.',
              },
            ].map(({ time, event, detail }) => (
              <Box key={time} sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.75, flexWrap: 'wrap', gap: 1 }}>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#8c52ff', fontWeight: 600 }}>{time}</Typography>
                  <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#f0f0f8', fontWeight: 700 }}>{event}</Typography>
                </Box>
                <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>{detail}</Typography>
              </Box>
            ))}
          </Stack>
        </Section>

        <Section title="The lawsuit: what happens when no system is listening">
          <Para>
            The Coolidge case resulted in a lawsuit filed by the parents of 19 children — alleging neglect, emotional abuse, and intentional infliction of emotional distress. These are not abstract legal categories. Emotional distress in young children is a clinical finding: disrupted sleep, regression in development, fear of authority figures, anxiety in caregiving settings.
          </Para>
          <Para>
            For the caregiver, a multi-plaintiff lawsuit of this scale represents financial exposure that cannot be insured away with a standard policy. Attorney fees, settlements across 19 families, potential punitive damages for intentional conduct — the number grows fast. For the parents, it means years of legal proceedings on top of the immediate task of repairing damage done to their children's sense of safety.
          </Para>
          <Para>
            For Sara MacDowell, it means explaining to her five children — the ones she trusted to a woman found on Facebook, around the corner from their home — that the person they were with was threatening them in ways they were too young to report.
          </Para>

          <Box sx={{ p: 2.5, bgcolor: '#13131f', border: '1px solid rgba(255,107,107,0.15)', borderLeft: '3px solid #ff6b6b', borderRadius: 2, mb: 3 }}>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 14, color: '#f0f0f8', mb: 0.75 }}>
              The recording that exposed the abuse came from a parent's phone.
            </Typography>
            <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, color: '#9499b0', lineHeight: 1.6 }}>
              A parent should not need to covertly record their child's caregiver to find out what is happening. That is not a safety system — it is a failure of every system that should have been in place before that moment.
            </Typography>
          </Box>
        </Section>

        <Section title="In-home daycares: the monitoring gap that lawsuits expose">
          <Para>
            Licensed commercial daycare centers face state inspections, staffing requirements, and increasingly, camera mandates like Minnesota's 2026 law. In-home daycares — the ones found on Facebook, affordable and local and trusted on the basis of a conversation and a playroom — operate in a different environment.
          </Para>
          <Para>
            They are not less dangerous. They are less monitored. The same behavioral patterns that emerge in commercial settings — threatening language, emotional intimidation, verbal abuse used as a control mechanism — emerge in in-home settings without any of the institutional oversight that might, at minimum, create a paper trail.
          </Para>
          <Para>
            Audio monitoring in an in-home daycare does not require cameras or complex installation. A microphone-enabled device analyzed by behavioral AI provides the same real-time detection capability regardless of the setting. The technology does not care whether the building is a licensed facility or a house around the corner.
          </Para>
          <Para>
            What it detects is the same: a voice, threatening a child, in a sentence that no caregiver should ever say.
          </Para>
        </Section>

        {/* Source attribution */}
        <Box sx={{ p: 2.5, bgcolor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 2, mb: 5 }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 600, fontSize: 12, color: '#9499b0', mb: 1.5 }}>Sources</Typography>
          <Stack spacing={1}>
            {[
              { label: 'ABC15 Arizona / YouTube: Arizona parents sue child care provider over alleged abuse', url: 'https://www.youtube.com/watch?v=jlhkJPPmiQE' },
            ].map(({ label, url }) => (
              <Box key={url} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: 'rgba(148,153,176,0.4)', mt: 0.7, flexShrink: 0 }} />
                <Typography
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 12, color: '#9499b0', lineHeight: 1.5, textDecoration: 'none', '&:hover': { color: '#8c52ff', textDecoration: 'underline' } }}
                >
                  {label}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.06)', mb: 5 }} />

        <Box sx={{ p: 4, bgcolor: '#13131f', border: '1px solid rgba(140,82,255,0.2)', borderRadius: 3, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontWeight: 800, fontSize: 20, color: '#f0f0f8', mb: 1 }}>
            A parent's phone shouldn't be the safety system.
          </Typography>
          <Typography sx={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 14, color: '#9499b0', mb: 3, lineHeight: 1.65 }}>
            Threatening language is one of the clearest signals audio AI detects. SMCLA flags it the first time it's spoken — before 19 families have a reason to sue, and before a child learns to be afraid of the dark.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/contact')} sx={{ px: 4, py: 1.5, fontWeight: 700, fontSize: 15, bgcolor: '#8c52ff', color: '#ffffff', borderRadius: 2, '&:hover': { bgcolor: '#7a45e0' } }}>
            Book a Demo →
          </Button>
        </Box>
      </Container>
      <Footer />
    </Box>
  );
}
